import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import InfoTip from './InfoTip';

import './SharedRowCard.scss';

// "Shares a row with": the members a creator sees as one Notification Center row, which member's
// words it renders with, and a picker that shows every notification that cannot join and why. It is
// only offered under an aggregated row with a row anchor, because those are what a group agrees on.
const SharedRowCard = ({ type, membership, canShare, isThread, anchor, onOpen, onCreate, onUpdate, onLeave, onLoadCandidates }) => {
  const [picker, setPicker] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => setPicker(null), [membership?.groupKey, membership?.members.length]);

  const openPicker = () => {
    setSearch('');
    setPicker({ loading: true, compatible: [], nearMisses: [] });
    onLoadCandidates()
      .then(resp => setPicker({ loading: false, ...resp }))
      .catch(() => setPicker(null));
  };

  const matches = candidate => !search.trim() || `${candidate.type} ${candidate.name}`.toLowerCase().includes(search.trim().toLowerCase());
  const isOwner = membership?.role === 'owner';

  return (
    <div className='card shared-row-card'>
      <div className='card-head'>
        <span className='card-title'>
          Shares a row with
          <InfoTip message='Several notifications a creator sees as one Notification Center row. One member writes the sentence; the rest render it. Push and email stay each notification&rsquo;s own.' />
        </span>
        <span className='card-note'>{membership ? membership.groupKey : 'not shared'}</span>
      </div>

      {!canShare && !membership && (
        <div className='shared-note'>
          {isThread
            ? 'A Live Thread needs a row anchor before it can share a row.'
            : 'Only an aggregated row with a row anchor can share a row. Set the Notification Center row to Aggregated row and give it a row anchor on the plan.'}
        </div>
      )}

      {canShare && !membership && (
        <div className='shared-body'>
          <div className='shared-note'>
            Nothing shares this row. A group keyed on <code>{anchor}</code> would put every member&rsquo;s events
            {anchor !== 'all' && ` for one ${anchor.split('.')[0]}`}{' '}
            {isThread ? 'into one row for that thing, rewritten as its status moves.' : 'into one row per UTC day.'}
          </div>
          <button type='button' className='quiet' onClick={() => onCreate()}>
            Open a shared row
          </button>
        </div>
      )}

      {membership && (
        <div className='shared-body'>
          <div className='shared-row-key'>
            Row key <code>{membership.rowKey}</code>
          </div>
          <div className='member-chips'>
            {membership.members.map(member => (
              <span key={member.type} className={cn('member-chip', member.role, { current: member.type === type })}>
                <button type='button' className='member-name' onClick={() => member.type !== type && onOpen(member.type)}>
                  {member.type}
                </button>
                <span className='member-role'>{member.type === type ? 'this entry' : member.role === 'owner' ? 'owns the words' : 'follower'}</span>
                {isOwner && member.type !== type && (
                  <button type='button' className='member-remove' onClick={() => onUpdate({ removeTypes: [member.type] })}>
                    ×
                  </button>
                )}
              </span>
            ))}
            {isOwner && canShare && (
              <button type='button' className={cn('member-chip add', { open: !!picker })} onClick={() => (picker ? setPicker(null) : openPicker())}>
                + Add a type
              </button>
            )}
          </div>

          {isOwner && membership.members.length > 1 && (
            <div className='shared-controls'>
              <label>
                Words live on
                <select value={membership.wordsLiveOnType} onChange={e => onUpdate({ wordsLiveOnType: e.target.value })}>
                  {membership.members.map(member => (
                    <option key={member.type} value={member.type}>
                      {member.type}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {!isOwner && (
            <div className='shared-note'>
              The sentence lives on{' '}
              <button type='button' className='link' onClick={() => onOpen(membership.wordsLiveOnType)}>
                {membership.wordsLiveOnType} ↗
              </button>
              . Move ownership here from that entry to write it from this one.
            </div>
          )}

          <button type='button' className='quiet' onClick={onLeave}>
            Leave the shared row
          </button>
        </div>
      )}

      {picker && membership && (
        <div className='type-picker'>
          <input className='picker-search' value={search} placeholder='Search notifications' onChange={e => setSearch(e.target.value)} autoFocus />
          <div className='picker-filter'>
            {membership.audience} · Aggregated · {membership.rowAnchor} · Per {membership.centerWindow} UTC
          </div>
          {picker.loading && <div className='picker-empty'>Looking…</div>}
          {!picker.loading && (
            <>
              <div className='picker-section compatible'>compatible · {picker.compatible.filter(matches).length}</div>
              {picker.compatible.filter(matches).map(candidate => (
                <button key={candidate.type} type='button' className='picker-row' onClick={() => onUpdate({ addTypes: [candidate.type] })}>
                  <span className='picker-mark ok'>✓</span>
                  <span className='picker-name'>
                    <code>{candidate.type}</code>
                    <span>{candidate.name}</span>
                  </span>
                </button>
              ))}
              <div className='picker-section'>near misses · not eligible · {picker.nearMisses.filter(matches).length}</div>
              {picker.nearMisses.filter(matches).map(candidate => (
                <div key={candidate.type} className='picker-row near-miss'>
                  <span className='picker-mark'>—</span>
                  <span className='picker-name'>
                    <code>{candidate.type}</code>
                    <span>{candidate.name}</span>
                  </span>
                  <span className='picker-reason'>{candidate.problems[0]}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

SharedRowCard.propTypes = {
  type: PropTypes.string.isRequired,
  membership: PropTypes.object,
  canShare: PropTypes.bool.isRequired,
  isThread: PropTypes.bool,
  anchor: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  onLoadCandidates: PropTypes.func.isRequired
};

export default SharedRowCard;
